const API_URL = process.env.REACT_APP_API_URL;

export type AssignedPlayer = {
  sn: string;
  location: string;
};

export type User = {
  id: number;
  username: string;
  role: "admin" | "user";
  customerName?: string;
  customerId?: string;
  assigned_players: AssignedPlayer[];
};

export type LoginResponse = {
  token: string;
  user: User;
};

export type Content = {
  file_name: string;
  content_name: string;
  created_at: string;
};

// NEU: Typ-Definition für einen Zeitplaneintrag
export type ScheduleEntry = {
  id: number; // SERIAL PRIMARY KEY im Backend
  player_sn: string;
  day_of_week: number; // 1=Montag, ..., 7=Sonntag
  start_time: string; // "HH:MM:SS"
  feed_id: string; // UUID
  feed_external_name: string;
  created_at: string; // Timestamp
};


async function apiFetch(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (options.headers && typeof options.headers === "object" && !Array.isArray(options.headers)) {
    Object.assign(headers, options.headers);
  }

  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (!res.ok) {
    let errorData: any;
    try {
      errorData = await res.json();
    } catch (e) {
      errorData = await res.text();
    }
    throw { status: res.status, data: errorData, message: errorData.message || res.statusText };
  }
  if (res.status === 204) return null;
  return res.json();
}

/* ---------------------------------------------------------- */
// Zugriff auf Endpunkte des VPS (node.js)
/* ---------------------------------------------------------- */

/* ---------------------------------------------------------- */
// Authentifizierung
/* ---------------------------------------------------------- */
export async function login(username: string, password: string): Promise<LoginResponse> {
  const res = await apiFetch("/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
  return res;
}

/* ---------------------------------------------------------- */
// Players
/* ---------------------------------------------------------- */
export async function fetchPlayersByCustomer(customerId: string) {
  return apiFetch(`/customers/${customerId}/players`);
}

export async function createPlayer(customerId: string, sn: string, location: string) {
  return apiFetch(`/customers/${customerId}/players`, {
    method: "POST",
    body: JSON.stringify({ sn, location }),
  });
}

export async function deletePlayer(customerId: string, playerSN: string) {
  return apiFetch(`/customers/${customerId}/players/${playerSN}`, {
    method: "DELETE",
  });
}

export async function fetchPlayersForUser(userId: number) {
  return apiFetch(`/users/${userId}/players`);
}


/* ---------------------------------------------------------- */
// Content
/* ---------------------------------------------------------- */
export async function fetchAllowedContent(playerSN: string) {
  const res = await apiFetch(`/allowed/${playerSN}`);
  return res.allowed || [];
}

export async function fetchAllContent(): Promise<Content[]> {
  return apiFetch(`/contents`);
}

export async function updateAllowedContentForPlayer(playerSN: string, contentNames: string[]) {
  return apiFetch(`/players/${playerSN}/allowed-content`, {
    method: "PATCH",
    body: JSON.stringify({ contentNames }),
  });
}


/* ---------------------------------------------------------- */
// Feeds
/* ---------------------------------------------------------- */
export async function fetchFeeds(playerSN: string) {
  return apiFetch(`/players/${playerSN}/feeds`);
}

export async function createFeed(playerSN: string, externalName: string, contentIds: string[] = []) {
  return apiFetch(`/players/${playerSN}/feeds`, {
    method: "POST",
    body: JSON.stringify({ externalName, contentIds }),
  });
}

export async function updateFeedContents(playerSN: string, feedId: string, contentIds: string[]) {
  return apiFetch(`/players/${playerSN}/feeds/${feedId}/contents`, {
    method: "PATCH",
    body: JSON.stringify({ contentIds }),
  });
}

export async function renameFeed(playerSN: string, feedId: string, externalName: string) {
  return apiFetch(`/players/${playerSN}/feeds/${feedId}/rename`, {
    method: "PATCH",
    body: JSON.stringify({ externalName }),
  });
}

export async function deleteFeed(playerSN: string, feedId: string) {
  return apiFetch(`/players/${playerSN}/feeds/${feedId}`, {
    method: "DELETE",
  });
}

export async function startFeed(playerId: string, feedId: string, externalName: string) {
  const res = await fetch(`${API_URL}/startFeed`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ playerId, feedId, externalName }),
  });
  if (!res.ok) {
    let errorBody: any;
    try {
      errorBody = await res.json();
    } catch (e) {
      errorBody = await res.text();
    }
    throw errorBody;
  }
  return res.json();
}

/* ---------------------------------------------------------- */
// Customers
/* ---------------------------------------------------------- */
export async function fetchCustomers() {
  return apiFetch("/customers");
}

export async function fetchAllCustomers() {
  return apiFetch("/customers");
}

export async function createCustomer(name: string) {
  return apiFetch("/customers", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export async function deleteCustomer(customerId: string) {
  return apiFetch(`/customers/${customerId}`, {
    method: "DELETE",
  });
}

/* ---------------------------------------------------------- */
// Users
/* ---------------------------------------------------------- */
export async function fetchUsersByCustomer(customerId: string): Promise<User[]> {
  return apiFetch(`/customers/${customerId}/users`);
}

export async function createUser(
  customerId: string,
  username: string,
  password: string,
  role: string,
  assignedPlayers: string[] = []
): Promise<User> {
  return apiFetch(`/customers/${customerId}/users`, {
    method: "POST",
    body: JSON.stringify({ username, password, role, assignedPlayers }),
  });
}

export async function deleteUser(customerId: string, userId: number) {
  return apiFetch(`/customers/${customerId}/users/${userId}`, {
    method: "DELETE",
  });
}

export async function resetUserPassword(customerId: string, userId: number, password: string) {
  return apiFetch(`/customers/${customerId}/users/${userId}/reset`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function updateUser(
  customerId: string,
  userId: number,
  updates: { username?: string; role?: string; password?: string; assignedPlayers?: string[] }
): Promise<User> {
  return apiFetch(`/customers/${customerId}/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

/* ---------------------------------------------------------- */
// Schedules
/* ---------------------------------------------------------- */
// Zeitplan für einen Player abrufen
export async function fetchSchedulesForPlayer(playerSN: string): Promise<ScheduleEntry[]> {
  return apiFetch(`/players/${playerSN}/schedules`);
}

// Zeitplaneintrag erstellen
export async function createScheduleEntry(
  playerSN: string,
  day_of_week: number,
  start_time: string,
  feed_id: string,
  feed_external_name: string
): Promise<ScheduleEntry> {
  return apiFetch(`/players/${playerSN}/schedules`, {
    method: "POST",
    body: JSON.stringify({ day_of_week, start_time, feed_id, feed_external_name }),
  });
}

// Zeitplaneintrag löschen
export async function deleteScheduleEntry(playerSN: string, scheduleId: number) {
  return apiFetch(`/players/${playerSN}/schedules/${scheduleId}`, {
    method: "DELETE",
  });
}
